import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import ApiErrorAlert from '../common/ApiErrorAlert';
import { resolveAcademicYearForRegistration } from '../../config/gradeEnum';
import { fetchGrades, GradeDto } from '../../services/gradeApi';
import { siteLogo, SITE_NAME } from '../../config/branding';

const Register: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [gradeId, setGradeId] = useState('');
  const [grades, setGrades] = useState<GradeDto[]>([]);
  const [gradesLoading, setGradesLoading] = useState(true);
  const [gradesError, setGradesError] = useState<string | null>(null);
  const [error, setError] = useState('');
  const { register, isLoading } = useAuth();
  const navigate = useNavigate();

  const loadGrades = useCallback(async () => {
    setGradesLoading(true);
    setGradesError(null);
    const result = await fetchGrades();
    if (result.ok) {
      setGrades(result.grades);
    } else {
      setGrades([]);
      setGradesError(result.message || 'تعذر تحميل المراحل الدراسية');
    }
    setGradesLoading(false);
  }, []);

  useEffect(() => {
    void loadGrades();
  }, [loadGrades]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('كلمات المرور غير متطابقة');
      return;
    }

    const selectedGrade = grades.find((g) => g.id === gradeId);
    if (!selectedGrade) {
      setError('الرجاء اختيار الفرقة الدراسية');
      return;
    }

    if (!phoneNumber) {
      setError('رقم الهاتف مطلوب');
      return;
    }

    const academicYear = resolveAcademicYearForRegistration(selectedGrade);
    if (!academicYear) {
      setError('تعذر ربط الفرقة المختارة بنظام التسجيل. يرجى التواصل مع الدعم.');
      return;
    }

    try {
      // Backend RegisterDto: AcademicYear = GradeEnum (e.g. Secondary1), not GRADE-{n} code
      const result = await register(
        name,
        email,
        password,
        confirmPassword,
        phoneNumber,
        academicYear,
        selectedGrade.id
      );

      if (result.success) {
        navigate('/login', {
          state: {
            registrationSuccess: true,
            message: 'تم إنشاء حسابك بنجاح! يمكنك الآن تسجيل الدخول.',
            email: email
          }
        });
      } else {
        setError(result.error || 'فشل إنشاء الحساب. الرجاء المحاولة مرة أخرى.');
      }
    } catch {
      setError('حدث خطأ غير متوقع. الرجاء المحاولة مرة أخرى.');
    }
  };

  const handleRetry = () => {
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-emerald-50/30 to-white flex items-start justify-center p-4" dir="rtl">
      <div className="w-full max-w-xl mx-auto rounded-3xl overflow-hidden bg-white border border-emerald-100 shadow-[0_4px_24px_rgba(0,0,0,0.08)] my-8">
        <div className="w-full p-8 md:p-10">
          <div className="mb-8 flex flex-col items-center text-center">
            <img
              src={siteLogo}
              alt={SITE_NAME}
              className="h-16 sm:h-20 w-auto object-contain mb-5"
            />
            <h1 className="font-el-messiri text-3xl sm:text-4xl font-bold text-slate-900 mb-2">إنشاء حساب</h1>
            <div className="h-1 w-16 rounded-full bg-gradient-to-r from-emerald-600 to-amber-500 mb-3" aria-hidden />
            <p className="font-cairo text-slate-600">ابدأ رحلة التعلم اليوم</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && <ApiErrorAlert message={error} onRetry={handleRetry} />}

            <div className="space-y-5">
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1 font-cairo">
                  الاسم الكامل
                </label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="block w-full px-4 py-3.5 rounded-xl border border-emerald-100 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-400 transition-colors text-sm font-cairo"
                  placeholder="أدخل اسمك الكامل"
                />
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1 font-cairo">
                  البريد الإلكتروني
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="block w-full px-4 py-3.5 rounded-xl border border-emerald-100 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-400 transition-colors text-sm font-cairo"
                  placeholder="أدخل بريدك الإلكتروني"
                />
              </div>

              <div>
                <label htmlFor="phoneNumber" className="block text-sm font-medium text-slate-700 mb-1 font-cairo">
                  رقم الهاتف
                </label>
                <input
                  id="phoneNumber"
                  name="phoneNumber"
                  type="tel"
                  required
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  className="block w-full px-4 py-3.5 rounded-xl border border-emerald-100 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-400 transition-colors text-sm font-cairo"
                  placeholder="أدخل رقم هاتفك"
                />
              </div>

              <div>
                <label htmlFor="grade" className="block text-sm font-medium text-slate-700 mb-1 font-cairo">
                  الفرقة الدراسية
                </label>
                {gradesLoading ? (
                  <div className="flex items-center gap-2 rounded-xl border border-emerald-100 bg-emerald-50/50 px-4 py-3.5">
                    <div
                      className="h-5 w-5 rounded-full border-2 border-emerald-100 border-t-emerald-600 animate-spin"
                      aria-hidden
                    />
                    <span className="font-cairo text-sm text-slate-600">جاري تحميل الفرق الدراسية...</span>
                  </div>
                ) : gradesError ? (
                  <div className="space-y-2">
                    <p className="font-cairo text-sm text-red-700">{gradesError}</p>
                    <button
                      type="button"
                      onClick={() => void loadGrades()}
                      className="font-cairo text-sm font-semibold text-emerald-700 hover:text-emerald-800"
                    >
                      إعادة المحاولة
                    </button>
                  </div>
                ) : (
                  <select
                    id="grade"
                    name="grade"
                    required
                    value={gradeId}
                    onChange={(e) => setGradeId(e.target.value)}
                    disabled={grades.length === 0}
                    className="block w-full px-4 py-3.5 rounded-xl border border-emerald-100 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-400 transition-colors text-sm font-cairo disabled:opacity-60"
                  >
                    <option value="">
                      {grades.length === 0 ? 'لا توجد فرق دراسية متاحة' : 'اختر فرقتك الدراسية'}
                    </option>
                    {grades.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-slate-700 mb-1 font-cairo">
                  كلمة المرور
                </label>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="block w-full px-4 py-3.5 rounded-xl border border-emerald-100 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-400 transition-colors text-sm font-cairo"
                  placeholder="أنشئ كلمة مرور"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-700 mb-1 font-cairo">
                  تأكيد كلمة المرور
                </label>
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="block w-full px-4 py-3.5 rounded-xl border border-emerald-100 bg-white shadow-[0_2px_8px_rgba(0,0,0,0.04)] focus:ring-2 focus:ring-emerald-600/30 focus:border-emerald-400 transition-colors text-sm font-cairo"
                  placeholder="أكد كلمة المرور"
                />
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isLoading || gradesLoading || !!gradesError || grades.length === 0}
                className={`w-full flex justify-center items-center gap-2 py-3.5 px-4 rounded-xl text-base font-bold text-emerald-950 bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 shadow-lg shadow-black/20 ring-2 ring-amber-200/50 transition hover:brightness-105 hover:scale-[1.01] active:scale-[0.99] font-cairo focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amber-500/80 ${isLoading || gradesLoading ? 'opacity-70 cursor-not-allowed hover:scale-100' : ''}`}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin h-5 w-5 text-emerald-950" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري إنشاء الحساب...
                  </>
                ) : (
                  'إنشاء الحساب'
                )}
              </button>
            </div>
          </form>

          <div className="mt-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-emerald-100" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white font-cairo text-slate-500">لديك حساب بالفعل؟</span>
              </div>
            </div>

            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="inline-flex items-center justify-center rounded-xl px-6 py-3 text-sm font-bold text-white bg-gradient-to-r from-emerald-700 via-emerald-800 to-emerald-900 shadow-md shadow-emerald-900/25 ring-1 ring-emerald-600/30 transition hover:brightness-105 hover:scale-[1.02] active:scale-[0.98] font-cairo"
              >
                تسجيل الدخول
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
